// ── 정산관리 모달 핸들러 훅 ───────────────────────────────────────────────────
// 마케터 계좌/세금정보 편집 모달의 상태·핸들러 모음
// 은행코드 CRUD는 동적 관리(TicketTypeManagement)로 이동됨

import { useState, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';

/**
 * @param {object} deps
 * @param {Function} deps.showToast      - 토스트 표시 함수
 * @param {Function} deps.fetchBankCodes - 활성 은행코드 목록 재조회 (부모 훅 제공)
 * @param {Function} deps.fetchList      - 정산 목록 재조회 (부모 훅 제공)
 * @param {string}   deps.companyCode    - 현재 회사 코드 (계좌·세금정보 분리 기준)
 */
export function usePayoutModalHandlers({ showToast, fetchBankCodes, fetchList, companyCode = 'PINE' }) {

  // ── 마케터별 계좌/세금정보 편집 모달 상태 ─────────────────────────────────
  const [editModal, setEditModal]       = useState(null); // { recordId, userId, userName, amount, ... }
  const [bankAccounts, setBankAccounts] = useState([]);
  const [taxInfoList, setTaxInfoList]   = useState([]);

  // ── 계좌/세금정보 편집 모달 열기 ─────────────────────────────────────────
  // companyCode 기준으로 해당 회사의 계좌·세금정보만 조회
  const openEditModal = useCallback(async (record) => {
    setEditModal(record);
    // 다른 마케터 데이터가 잠깐 보이는 문제 방지 — 즉시 초기화
    setBankAccounts([]);
    setBankAccounts([]);
    setTaxInfoList([]);
    const [baResult, tiResult] = await Promise.allSettled([
      apiClient.get(`/api/admin/payout/bank-accounts?userId=${record.user_id}&companyCode=${companyCode}`),
      apiClient.get(`/api/admin/payout/tax-info?userId=${record.user_id}&companyCode=${companyCode}`),
    ]);

    const baRes = baResult.status === 'fulfilled' ? baResult.value : null;
    if (baRes?.ok && baRes.data?.success) {
      setBankAccounts(baRes.data.data || []);
    } else {
      showToast(baRes?.data?.message || '계좌정보 조회 실패', 'error');
    }

    const tiRes = tiResult.status === 'fulfilled' ? tiResult.value : null;
    if (tiRes?.ok && tiRes.data?.success) {
      setTaxInfoList(tiRes.data.data || []);
    } else {
      showToast(tiRes?.data?.message || '세금정보 조회 실패', 'error');
    }
  }, [companyCode, showToast]);

  // ── 편집 모달 닫기 ────────────────────────────────────────────────────────
  const closeEditModal = useCallback(() => {
    setEditModal(null);
    setBankAccounts([]);
    setTaxInfoList([]);
  }, []);

  // ── 계좌/세금정보 연결 저장 ───────────────────────────────────────────────
  const linkRecord = useCallback(async (recordId, bankAccountId, taxInfoId) => {
    try {
      const res = await apiClient.put(`/api/admin/payout/records/${recordId}/link`, {
        bankAccountId: bankAccountId || null,
        taxInfoId: taxInfoId || null,
      });
      if (res.data?.success) {
        showToast('연결 완료', 'success');
        fetchList(); // 목록 새로고침
        closeEditModal();
      } else {
        showToast(res.data?.message || '연결 실패', 'error');
      }
    } catch (_) {
      showToast('연결 실패', 'error');
    }
  }, [showToast, fetchList, closeEditModal]);

  // ── 새 계좌 추가 — companyCode 포함하여 등록 ──────────────────────────────
  const addBankAccount = useCallback(async (userId, bankCodeId, accountNumber, accountHolder, options = {}) => {
    try {
      const res = await apiClient.post('/api/admin/payout/bank-accounts', {
        userId,
        bankCodeId,
        accountNumber,
        accountHolder,
        companyCode,
        effectiveFrom: options.effectiveFrom,
        changeMode: options.changeMode,
        reason: options.reason,
      });
      if (res.data?.success) {
        showToast('계좌 추가 완료', 'success');
        const r2 = await apiClient.get(`/api/admin/payout/bank-accounts?userId=${userId}&companyCode=${companyCode}`);
        if (r2.data?.success) setBankAccounts(r2.data.data || []);
        return res.data.id;
      } else {
        showToast(res.data?.message || '계좌 추가 실패', 'error');
      }
    } catch (_) {
      showToast('계좌 추가 실패', 'error');
    }
    return null;
  }, [showToast, companyCode]);

  // ── 새 세금정보 추가 — companyCode 포함하여 등록 ─────────────────────────
  const addTaxInfo = useCallback(async (userId, name, residentNumber, taxRate, options = {}) => {
    try {
      const res = await apiClient.post('/api/admin/payout/tax-info', {
        userId,
        name,
        residentNumber,
        taxRate,
        companyCode,
        // 과세유형 (스위치 ON 화면에서만 채워짐 — 미지정이면 legacy 개인 3.3%)
        taxTypeCode: options.taxTypeCode || undefined,
        businessRegistrationNumber: options.businessRegistrationNumber || undefined,
        businessName: options.businessName || undefined,
        effectiveFrom: options.effectiveFrom,
        changeMode: options.changeMode,
        reason: options.reason,
      });
      if (res.data?.success) {
        showToast('세금정보 추가 완료', 'success');
        const r2 = await apiClient.get(`/api/admin/payout/tax-info?userId=${userId}&companyCode=${companyCode}`);
        if (r2.data?.success) setTaxInfoList(r2.data.data || []);
        return res.data.id;
      } else {
        showToast(res.data?.message || '세금정보 추가 실패', 'error');
      }
    } catch (_) {
      showToast('세금정보 추가 실패', 'error');
    }
    return null;
  }, [showToast, companyCode]);

  // ── 계좌 삭제 (soft delete) ───────────────────────────────────────────────
  const deleteBankAccount = useCallback(async (id, userId) => {
    try {
      const res = await apiClient.del(`/api/admin/payout/bank-accounts/${id}`);
      if (res.data?.success) {
        showToast('계좌 삭제 완료', 'success');
        const r2 = await apiClient.get(`/api/admin/payout/bank-accounts?userId=${userId}&companyCode=${companyCode}`);
        if (r2.data?.success) setBankAccounts(r2.data.data || []);
      } else {
        showToast(res.data?.message || '삭제 실패', 'error');
      }
    } catch (_) {
      showToast('삭제 실패', 'error');
    }
  }, [showToast, companyCode]);

  // ── 세금정보 삭제 (soft delete) ───────────────────────────────────────────
  const deleteTaxInfo = useCallback(async (id, userId) => {
    try {
      const res = await apiClient.del(`/api/admin/payout/tax-info/${id}`);
      if (res.data?.success) {
        showToast('세금정보 삭제 완료', 'success');
        const r2 = await apiClient.get(`/api/admin/payout/tax-info?userId=${userId}&companyCode=${companyCode}`);
        if (r2.data?.success) setTaxInfoList(r2.data.data || []);
      } else {
        showToast(res.data?.message || '삭제 실패', 'error');
      }
    } catch (_) {
      showToast('삭제 실패', 'error');
    }
  }, [showToast, companyCode]);

  return {
    // 계좌/세금정보 편집 모달
    editModal, bankAccounts, taxInfoList,
    openEditModal, closeEditModal, linkRecord,
    addBankAccount, addTaxInfo, deleteBankAccount, deleteTaxInfo,
  };
}
